import { Link, router } from '@inertiajs/react'
import { Plug, Trash2, Users } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import { shouldSkipRowNavigation } from '@/frontend/utils/clickableRow'
import type { AssignWorker } from '@/frontend/pages/Dashboard/Integrations/types'

// Cabeçalho de tabela canônico do portal (design.md: th 11/650 uppercase baixa).
const TH_CLS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-faint-foreground'

interface CredentialWorkersTableProps {
  workers: AssignWorker[]
  credentialId: string
  canManage: boolean
}

const initialOf = (name: string) => name.trim().charAt(0).toUpperCase()

export default function CredentialWorkersTable({ workers, credentialId, canManage }: CredentialWorkersTableProps) {
  const handleAssign = (workerId: string) => {
    router.post('/dashboard/credentials/bulk_assign_agents', {
      credential_id: credentialId,
      worker_ids: [workerId],
    }, { preserveScroll: true })
  }

  const handleRemove = (workerId: string) => {
    if (!window.confirm('Tem certeza que deseja remover este colaborador?')) return
    router.delete(`/dashboard/credentials/${credentialId}/remove_worker`, {
      data: { worker_id: workerId },
      preserveScroll: true,
    })
  }

  return (
    <>
      <div className="mb-4 flex items-center gap-2 text-sm font-medium text-foreground">
        <Users className="size-4 text-muted-foreground" strokeWidth={1.75} />
        Colaboradores digitais
      </div>

      {workers.length === 0 ? (
        <p className="py-4 text-center text-[13px] text-muted-foreground">
          Nenhum colaborador digital disponível.
        </p>
      ) : (
        <Table className="text-[13px]">
          <TableHeader>
            <TableRow>
              <TableHead className={TH_CLS}>Nome</TableHead>
              <TableHead className={`hidden sm:table-cell ${TH_CLS}`}>Cargo</TableHead>
              <TableHead className={`hidden sm:table-cell ${TH_CLS}`}>Status</TableHead>
              <TableHead className="w-28"><span className="sr-only">Ações</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workers.map((worker) => (
              <TableRow
                key={worker.id}
                className="cursor-pointer"
                onClick={(e) => {
                  if (shouldSkipRowNavigation(e)) return
                  router.visit(`/dashboard/digital_workers/${worker.id}`)
                }}
              >
                <TableCell className="whitespace-normal">
                  <Link
                    href={`/dashboard/digital_workers/${worker.id}`}
                    className="inline-flex items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Avatar className="size-6 shrink-0 text-[10px]">
                      {worker.avatar_url ? <AvatarImage alt="" src={worker.avatar_url} /> : null}
                      <AvatarFallback className="bg-agent text-agent-foreground" style={agentHueStyle(worker.id)}>
                        {initialOf(worker.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-foreground">{worker.name}</span>
                  </Link>
                </TableCell>
                <TableCell className="hidden text-muted-foreground sm:table-cell">{worker.job_title || '—'}</TableCell>
                <TableCell className="hidden sm:table-cell">
                  <Badge variant={worker.assigned ? 'success' : 'secondary'}>
                    {worker.assigned ? 'Ativo' : 'Inativo'}
                  </Badge>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {worker.assigned ? (
                    <Button
                      variant="ghost" size="sm"
                      disabled={!canManage}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemove(worker.id)}
                    >
                      <Trash2 className="mr-1 size-4" /> Remover
                    </Button>
                  ) : (
                    <Button
                      variant="outline" size="sm"
                      disabled={!canManage}
                      onClick={() => handleAssign(worker.id)}
                    >
                      <Plug className="mr-1 size-4" /> Associar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}
